import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/AuthContext';
import { uiLogger } from '../../utils/loggerInstance';
import { LoadingSpinnerFullscreen } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { useCurrentSubscriptionForAccess } from '../../hooks/useCurrentSubscriptionForAccess';
import { AccountLoadErrorCard } from './AccountLoadErrorCard';
import { ORGANIZATION_KEYS, SUBSCRIPTION_KEYS, USER_KEYS } from '../../constants/queryKeys';
import { PROFILE_ENTERPRISE_BILLING_PATH } from '../../services/billingEntitlementPolicy';

interface RequireBasePlanRouteProps {
  children: React.ReactNode;
}

const ALERT_ICON_PATH =
  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
const CARD_CLASS = 'flex flex-col items-center justify-center h-screen bg-background p-4';
const INNER_CARD_CLASS = 'max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center';

function canManageOrgBilling(role: string | null | undefined): boolean {
  return ['admin', 'owner'].includes(role || '');
}

const REDIRECTED_NO_PLAN_STATE = { redirectedNoPlan: true } as const;

function redirectOrgUserToPricing(
  navigate: (path: string, options?: { state?: object }) => void,
  isRedirectingRef: React.MutableRefObject<boolean>,
): void {
  uiLogger.info(
    'Organization admin/owner with no enterprise plan – redirecting to Enterprise Billing',
  );
  isRedirectingRef.current = true;
  navigate(PROFILE_ENTERPRISE_BILLING_PATH, { state: REDIRECTED_NO_PLAN_STATE });
}

function redirectPersonalUserToPricing(
  navigate: (path: string, options?: { state?: object }) => void,
  isRedirectingRef: React.MutableRefObject<boolean>,
): void {
  uiLogger.info('Personal user with no base plan – redirecting to pricing');
  isRedirectingRef.current = true;
  navigate('/profile/pricing', { state: REDIRECTED_NO_PLAN_STATE });
}

function getSubscriptionErrorScreenContent(): {
  heading: string;
  description: string;
  buttonLabel: string;
} {
  return {
    heading: "We couldn't load your subscription",
    description:
      "We couldn't load your subscription status. Please try again or contact support if the problem continues.",
    buttonLabel: 'Retry',
  };
}

function RouteMessageCard({
  heading,
  description,
  buttonLabel,
  onAction,
}: {
  heading: string;
  description: string;
  buttonLabel: string;
  onAction: () => void;
}): React.ReactElement {
  return (
    <div className={CARD_CLASS}>
      <div className={INNER_CARD_CLASS}>
        <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-destructive/10 mb-4'>
          <svg
            className='h-6 w-6 text-destructive'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d={ALERT_ICON_PATH}
            />
          </svg>
        </div>
        <h3 className='text-lg font-medium text-foreground mb-2'>{heading}</h3>
        <p className='text-sm text-muted-foreground mb-6'>{description}</p>
        <Button type='button' onClick={onAction}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}

function OrganizationRequiredCard({ onRefresh }: { onRefresh: () => void }): React.ReactElement {
  return (
    <RouteMessageCard
      heading='Organization Subscription Required'
      description='Your organization does not have an active subscription. Only an organization owner or administrator can add or upgrade a plan. Please contact your org admin to get a subscription, or refresh to check if one has been added.'
      buttonLabel='Refresh Status'
      onAction={onRefresh}
    />
  );
}

function shouldSkipRedirectCheck(
  authLoading: boolean,
  dataLoading: boolean,
  isAuthenticated: boolean,
  isRedirectingRef: React.MutableRefObject<boolean>,
  accountContextError: boolean,
  subscriptionError: boolean,
): boolean {
  return (
    authLoading ||
    dataLoading ||
    !isAuthenticated ||
    isRedirectingRef.current ||
    accountContextError ||
    subscriptionError
  );
}

function runRedirectCheck(
  isOrgUser: boolean,
  subscription: unknown,
  organizationRole: string | null | undefined,
  navigate: (path: string) => void,
  isRedirectingRef: React.MutableRefObject<boolean>,
): void {
  if (isOrgUser) {
    if (!subscription && canManageOrgBilling(organizationRole)) {
      redirectOrgUserToPricing(navigate, isRedirectingRef);
    }
    return;
  }
  if (!subscription) {
    redirectPersonalUserToPricing(navigate, isRedirectingRef);
  }
}

function logRouteErrorInDev(
  accountContextError: unknown,
  subscriptionError: unknown,
  organizationError: unknown,
): void {
  if (process.env.NODE_ENV !== 'development') return;
  const hasResponse = (e: unknown): e is { response?: { status?: number; data?: unknown } } =>
    typeof e === 'object' && e !== null && 'response' in e;
  uiLogger.error('[RequireBasePlanRoute] Showing error screen', {
    accountContextError,
    subscriptionError: !!subscriptionError,
    accountContextErrorDetail:
      accountContextError instanceof Error
        ? { message: accountContextError.message, name: accountContextError.name }
        : accountContextError,
    organizationErrorDetail:
      organizationError instanceof Error
        ? { message: organizationError.message, name: organizationError.name }
        : organizationError,
    subscriptionErrorDetail:
      subscriptionError instanceof Error
        ? { message: (subscriptionError as Error).message, name: (subscriptionError as Error).name }
        : subscriptionError,
    axiosStatus: hasResponse(accountContextError)
      ? accountContextError.response?.status
      : undefined,
    axiosData: hasResponse(accountContextError) ? accountContextError.response?.data : undefined,
  });
}

function getRouteScreen(
  isLoading: boolean,
  isAuthenticated: boolean,
  hasDataError: boolean,
  subscriptionError: boolean,
  accountContextError: unknown,
  organizationError: unknown,
  subscription: unknown,
  isOrgUser: boolean,
  organizationRole: string | null | undefined,
  onRetryAccountLoad: () => void,
  onRetrySubscriptionLoad: () => void,
  onRefreshOrganizationStatus: () => void,
  children: React.ReactNode,
): React.ReactNode {
  if (isLoading) return <LoadingSpinnerFullscreen size='xl' text='Loading your account...' />;
  if (!isAuthenticated) return null;
  if (hasDataError) {
    logRouteErrorInDev(accountContextError, subscriptionError, organizationError);
    if (!subscriptionError) {
      return <AccountLoadErrorCard onRetry={onRetryAccountLoad} fullScreen />;
    }
    const content = getSubscriptionErrorScreenContent();
    return (
      <RouteMessageCard
        heading={content.heading}
        description={content.description}
        buttonLabel={content.buttonLabel}
        onAction={onRetrySubscriptionLoad}
      />
    );
  }
  if (subscription) return <>{children}</>;
  if (isOrgUser && !canManageOrgBilling(organizationRole)) {
    return <OrganizationRequiredCard onRefresh={onRefreshOrganizationStatus} />;
  }
  return <LoadingSpinnerFullscreen size='xl' text='Redirecting to pricing...' />;
}

/**
 * Route guard that requires an active base plan (B2C or B2B subscription).
 * Uses a single hook that calls exactly one API: subscriptions/current (personal)
 * or subscriptions/enterprise/current (org), based on profile. Never calls both.
 *
 * Redirects to appropriate checkout if plan is missing.
 */
export const RequireBasePlanRoute: React.FC<RequireBasePlanRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const {
    subscription,
    isOrgUser,
    organizationRole,
    isLoading: dataLoading,
    error: subscriptionError,
    accountContextError,
    organizationError,
  } = useCurrentSubscriptionForAccess();
  const isRedirectingRef = useRef(false);

  const retryProfileAndOrgResolution = () => {
    void queryClient.invalidateQueries({ queryKey: USER_KEYS.profile() });
    void queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
    void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
  };

  const retrySubscriptionResolution = () => {
    void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
  };

  const refreshOrganizationSubscriptionStatus = () => {
    void queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
    void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
  };

  useEffect(() => {
    if (
      shouldSkipRedirectCheck(
        authLoading,
        dataLoading,
        isAuthenticated,
        isRedirectingRef,
        !!accountContextError,
        !!subscriptionError,
      )
    ) {
      return;
    }
    runRedirectCheck(isOrgUser, subscription, organizationRole, navigate, isRedirectingRef);
  }, [
    isAuthenticated,
    authLoading,
    dataLoading,
    isOrgUser,
    subscription,
    organizationRole,
    accountContextError,
    subscriptionError,
    navigate,
  ]);

  const isLoading = authLoading || dataLoading;
  const hasDataError = !!accountContextError || !!subscriptionError;
  return getRouteScreen(
    isLoading,
    isAuthenticated,
    hasDataError,
    !!subscriptionError,
    accountContextError,
    organizationError,
    subscription,
    isOrgUser,
    organizationRole,
    retryProfileAndOrgResolution,
    retrySubscriptionResolution,
    refreshOrganizationSubscriptionStatus,
    children,
  );
};
